import { LoadingButton } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useState } from 'react';
import { Focused } from 'react-credit-cards-2';
import { Controller, useFormState, UseFormReturn } from 'react-hook-form';
import Input from '~/components/Input';
import ModalTermOfContract from '~/modals/ModalTermOfContract';
import type { Inputs } from '../../types';
import CardComponent from '../../CardCredit';
import { containerButton, containerForm, headerContainer } from '../../styles';

interface CardFormProps {
  formMethods: UseFormReturn<Inputs>;
  focused: Focused;
  setFocused: Dispatch<SetStateAction<Focused>>;
  checkedTermOfContract: boolean;
  setCheckedTermOfContract: (value: boolean) => void;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  loading: boolean;
  showCard: boolean;
  paymentError: boolean | string;
  stepSelected: string;
  onConfirmStep: (currentStep: string) => void; // Nova prop para confirmar o pagamento da etapa atual
  paymentStepsState: Record<string, boolean>; // Adiciona o estado dos steps como prop
  organizationId?: string | null;
}

export default function CardForm({
  formMethods,
  focused,
  setFocused,
  checkedTermOfContract,
  setCheckedTermOfContract,
  handleSubmit,
  loading,
  showCard,
  paymentError,
  stepSelected,
  onConfirmStep: _onConfirmStep,
  paymentStepsState, // Usa o estado dos steps
  organizationId
}: CardFormProps) {
  const {
    control,
    formState: { errors, dirtyFields },
    getValues
  } = formMethods;
  const { isValid } = useFormState({ control });

  const isStepCompleted = paymentStepsState[stepSelected]; // Determina se o step está completo

  const [_savedValues, setSavedValues] = useState<Record<string, Record<string, any>>>({}); // Organiza os valores por step

  const _handleSaveValues = () => {
    const currentValues = getValues();
    setSavedValues((prev) => ({
      ...prev,
      [stepSelected]: { ...currentValues } // Salva os valores preenchidos para o step atual
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    try {
      await handleSubmit(e as any);
    } catch (error) {
      console.error('❌ Erro no submit do CardForm:', error);
    }
  };

  return (
    <>
      {showCard && (
        <Box sx={headerContainer}>
          <CardComponent control={control} focused={focused} />
        </Box>
      )}
      <form onSubmit={handleFormSubmit}>
        <Box sx={containerForm}>
          <Grid container spacing={{ sm: 1 }}>
            <Grid item xs={12}>
              <Controller
                name="number"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="Número do Cartão"
                    onFocus={() => setFocused('number')}
                    placeholder="XXXX XXXX XXXX XXXX"
                    mask="cardNumber"
                    isDirty={dirtyFields.number}
                    error={!!errors?.number?.message}
                    helperText={errors?.number?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Controller
            name="name"
            control={control}
            disabled={isStepCompleted}
            render={({ field }) => (
              <Input
                label="Nome no Cartão"
                onFocus={() => setFocused('name')}
                placeholder="exatamente como está impresso"
                isDirty={dirtyFields.name}
                error={!!errors?.name?.message}
                helperText={errors?.name?.message?.toString()}
                {...field}
              />
            )}
          />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Controller
                name="expiry"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="Expira"
                    onFocus={() => setFocused('expiry')}
                    placeholder="MM/AA"
                    mask="expiredDate"
                    isDirty={dirtyFields.expiry}
                    error={!!errors?.expiry?.message}
                    helperText={errors?.expiry?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="cvv"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="CVV"
                    onFocus={() => setFocused('cvc')}
                    placeholder="XXX"
                    mask="cvv"
                    isDirty={dirtyFields.cvv}
                    error={!!errors?.cvv?.message}
                    helperText={errors?.cvv?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Seção Endereço Fiscal */}
          <Box sx={{ marginTop: 3 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: 'primary.secodary' }}>
              Endereço Fiscal
            </Typography>
          </Box>

          {/* CEP com Número */}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Controller
                name="zipcode"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="CEP"
                    placeholder="XXXXX-XXX"
                    mask="zipcode"
                    isDirty={dirtyFields.zipcode}
                    error={!!errors?.zipcode?.message}
                    helperText={errors?.zipcode?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="numberAddress"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="Número"
                    placeholder="Número da residência"
                    isDirty={dirtyFields.numberAddress}
                    error={!!errors?.numberAddress?.message}
                    helperText={errors?.numberAddress?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Complemento com Estado/UF */}
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Controller
                name="additionalDetails"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="Complemento"
                    placeholder="Apto ou bloco"
                    isDirty={dirtyFields.additionalDetails}
                    error={!!errors?.additionalDetails?.message}
                    helperText={errors?.additionalDetails?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="state"
                control={control}
                disabled={isStepCompleted}
                render={({ field }) => (
                  <Input
                    label="Estado/UF"
                    placeholder="Digite primeiro o CEP"
                    inputProps={{ maxLength: 2 }}
                    isDirty={dirtyFields.state}
                    error={!!errors?.state?.message}
                    helperText={errors?.state?.message?.toString()}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Cidade (linha completa) */}
          <Controller
            name="city"
            control={control}
            disabled={isStepCompleted}
            render={({ field }) => (
              <Input
                label="Cidade"
                placeholder="Digite primeiro o CEP"
                isDirty={dirtyFields.city}
                error={!!errors?.city?.message}
                helperText={errors?.city?.message?.toString()}
                {...field}
              />
            )}
          />

          {/* Bairro (linha completa) */}
          <Controller
            name="neighborhood"
            control={control}
            disabled={isStepCompleted}
            render={({ field }) => (
              <Input
                label="Bairro"
                placeholder="Digite primeiro o CEP"
                isDirty={dirtyFields.neighborhood}
                error={!!errors?.neighborhood?.message}
                helperText={errors?.neighborhood?.message?.toString()}
                {...field}
              />
            )}
          />

          {/* Endereço (linha completa) */}
          <Controller
            name="street"
            control={control}
            disabled={isStepCompleted}
            render={({ field }) => (
              <Input
                label="Endereço"
                placeholder="Digite primeiro o CEP"
                isDirty={dirtyFields.street}
                error={!!errors?.street?.message}
                helperText={errors?.street?.message?.toString()}
                {...field}
              />
            )}
          />
        </Box>
        <ModalTermOfContract
          checkedTermOfContract={checkedTermOfContract}
          setCheckedTermOfContract={setCheckedTermOfContract}
          organizationId={organizationId}
        />
        <Box>
          <Typography>{paymentError}</Typography>
        </Box>
        <Box sx={containerButton}>
          <LoadingButton
            variant="contained"
            disabled={!checkedTermOfContract || !isValid || isStepCompleted || loading}
            loading={loading}
            type="submit"
          >
            {/* {isStepCompleted ? 'Etapa concluída' : 'Confirmar e continuar'} */}
            Confirmar e continuar
          </LoadingButton>
        </Box>
      </form>
    </>
  );
}
